import { useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch';
import { Facets } from '~/services/instantsearch';
import { useTranslation } from '~/shared/utils/translation';
import { useDrawerFilterTracking } from '~/features/productList/hooks/useDrawerFilterTracking';
import useOptimizedRefinementList from '~/shared/hooks/useOptimizedRefinementList';
import { PROMOTION_GROUPS, PromotionGroupKey } from './promotionGroups';

const normalizeValue = (value: string) => value.trim().toLowerCase();

export type SalesOfferSubItem = {
    key: string;
    label: string;
    value: string;
    isRefined: boolean;
    count: number;
};

export type SalesOfferGroupItem = {
    key: PromotionGroupKey;
    label: string;
    values: string[];
    isRefined: boolean;
    isIndeterminate: boolean;
    count: number;
    subItems: SalesOfferSubItem[];
};

export const useSalesOfferGroups = () => {
    const { translate } = useTranslation();
    const { status } = useInstantSearch();
    const { trackFilter } = useDrawerFilterTracking();
    const { allItems, refine } = useOptimizedRefinementList({
        attribute: Facets.ACTIVE_PROMO_TYPE,
    });
    const lastGroupItemsRef = useRef<SalesOfferGroupItem[]>([]);

    // Each product carries exactly one "winning" promotiontype value (never more than one at
    // once), so unlike a multi-value facet, summing several of this attribute's own per-value
    // counts can never double-count a product — it's already the exact union count. Confirmed
    // empirically: summing every raw value's count for a category exactly matched a manual
    // `filters` OR-query's nbHits for the same category.
    // Holds the last settled result while a search is in flight, since `allItems` can look empty before it resolves.
    const groupItems: SalesOfferGroupItem[] = (() => {
        if (status !== 'idle') return lastGroupItemsRef.current;

        const mapped = PROMOTION_GROUPS.map((group) => {
            const rawItems = group.values.flatMap((value) =>
                allItems.filter((item) => normalizeValue(item.value) === normalizeValue(value)),
            );

            const refinedCount = rawItems.filter((item) => item.isRefined).length;
            const isRefined = rawItems.length > 0 && refinedCount === rawItems.length;
            const hasChildren = group.values.length > 1;
            const isIndeterminate = hasChildren && refinedCount > 0 && !isRefined;
            const count = rawItems.reduce((sum, item) => sum + item.count, 0);

            return {
                key: group.key,
                label: translate(group.labelKey),
                // The facet's own spelling is what refine() needs, so prefer it over the configured one.
                values: rawItems.length ? rawItems.map((item) => item.value) : group.values,
                isRefined,
                isIndeterminate,
                count,
                subItems: hasChildren
                    ? rawItems.map((item) => ({
                          key: item.value,
                          label: item.label,
                          value: item.value,
                          isRefined: item.isRefined,
                          count: item.count,
                      }))
                    : [],
            };
        });

        const visible = allItems.length > 0 ? mapped.filter((group) => group.count > 0) : mapped;
        lastGroupItemsRef.current = visible;
        return visible;
    })();

    const selectedCount = groupItems.filter((item) => item.isRefined).length;
    const isAllRefined = groupItems.length > 0 && groupItems.every((item) => item.isRefined);
    const allCount = groupItems.reduce((sum, item) => sum + item.count, 0);

    const refineValues = (values: string[], shouldBeRefined: boolean) => {
        values.forEach((value) => {
            const item = allItems.find((i) => i.value === value);

            if (item && item.isRefined !== shouldBeRefined) {
                trackFilter(Facets.ACTIVE_PROMO_TYPE, value, { isRefined: item.isRefined });
                refine(value);
            }
        });
    };

    const handleGroupRefine = (key: string) => {
        const group = groupItems.find((item) => item.key === key);
        if (!group) return;

        refineValues(group.values, !group.isRefined);
    };

    const handleAllRefine = () =>
        refineValues(
            groupItems.flatMap((item) => item.values),
            !isAllRefined,
        );

    const handleSubItemRefine = (value: string) => {
        const item = allItems.find((i) => i.value === value);
        if (!item) return;

        trackFilter(Facets.ACTIVE_PROMO_TYPE, value, { isRefined: item.isRefined });
        refine(value);
    };

    return {
        groupItems,
        selectedCount,
        isAllRefined,
        allCount,
        handleGroupRefine,
        handleAllRefine,
        handleSubItemRefine,
    };
};
